/**
 * PatientRowActions — per-row 3-dot (kebab) overflow menu for the patient
 * list. Ports the Flutter `menuOptions()` builder in
 * `patient_list_screen.dart:739-973`, which feeds the row table's overflow
 * menu (`:1862`).
 *
 * Flutter renders two menu shapes keyed on the list-level archived filter:
 *
 *   Active view (`options`, :776-963), in order:
 *     1. Create Appointment   — `Manage_Patient_Appointments` add + !merged
 *     2. Print Patient sticker — always
 *     3. View Billing Details — `Manage_Patient_Appointments` add + !merged
 *     4. Edit / View Patient  — title flips on `Manage_Patients_Demographics` edit
 *     5. Archive Patient (red)— `Manage_Patients_Demographics` delete
 *
 *   Archived view (`archivedOptions`, :742-774):
 *     1. Edit Patient Details — unconditional (always "Edit", never "View")
 *
 * Scope note: items 1 (Create Appointment) and 2 (Print sticker) are NOT
 * wired yet — Create Appointment is a cross-domain handoff into the
 * scheduling create flow with a preset patient (no React wiring exists),
 * and Print sticker is a standalone org-detail-fetch + sticker-render
 * feature with no React equivalent. Both are tracked as follow-ups; the
 * remaining three items below are exact ports. Relative order of the
 * shipped items matches Flutter (Billing → Edit/View → Archive).
 */

import { useState } from 'react';
import { RowActionsTrigger } from './row-actions-trigger';
import { AnchoredPopover, AnchoredPopoverContent } from '@scripsteam/scrips-signal-ds';
import { Archive, Calendar, MoreHorizontal, Pencil, Plus, Printer, Receipt, type LucideIcon } from 'lucide-react';
import { PatientStickerDialog } from './patient-sticker-dialog';
import type { PatientDatum } from '@/types/patient-api';

export interface PatientRowActionsProps {
  patient: PatientDatum;
  /** List-level archived filter (Flutter `isArchived!`) — picks the menu shape. */
  isArchivedView: boolean;
  /** List-level merged filter (Flutter `isMerged`) — gates Billing/Create. */
  isMergedView: boolean;
  /** `Manage_Patients_Demographics` edit — flips Edit ↔ View title. */
  canEditDemographics: boolean;
  /** `Manage_Patients_Demographics` delete — shows Archive. */
  canArchive: boolean;
  /**
   * `Access_Patient_Billing` (view) — shows View Billing Details. Gated on
   * the SAME permission as the billing tab this shortcut navigates to
   * (patient-edit-page.tsx:91 / Flutter create_patient_screen.dart:909), so
   * the shortcut is never shown for a tab the user can't open. This is a
   * deliberate divergence from the Flutter row-action source, which gates it
   * on the appointments permission — see the parent's gate comment for why.
   */
  canViewBilling: boolean;
  /** `Manage_Patient_Appointments` add — shows Create Appointment (Flutter
   *  `patient_list_screen.dart:777-780`, gated with `!isMerged`). */
  canCreateAppointment: boolean;
  /** Org branding logo for the printable sticker — caller sources it from
   *  the auth store (`useAuthStore(s => s.user?.brandingUrl)`). */
  brandingUrl?: string | null;
  /** Show the sticker's DEV+beta-only print-dimension testing block.
   *  Parent passes `import.meta.env.DEV && !!user.isBeta`. */
  stickerDevTools?: boolean;
  /** Launch the calendar create-appointment flow prefilled with this
   *  patient (cross-domain handoff via the shared store). */
  onCreateAppointment: (patient: PatientDatum) => void;
  onEdit: (patientId: string) => void;
  onViewBilling: (patientId: string) => void;
  onArchive: (patientId: string) => void;
  /** Enrol this patient in a cohort (PROD-3206). Omit to hide the item; the parent passes it
   *  only when `isCohortEnrolmentOn()` is on (deploy flag + `?x=cohorts`). */
  onEnrolInCohort?: (patient: PatientDatum) => void;
}

export function PatientRowActions({
  patient,
  isArchivedView,
  isMergedView,
  canEditDemographics,
  canArchive,
  canViewBilling,
  canCreateAppointment,
  brandingUrl,
  stickerDevTools,
  onCreateAppointment,
  onEdit,
  onViewBilling,
  onArchive,
  onEnrolInCohort,
}: PatientRowActionsProps) {
  const [open, setOpen] = useState(false);
  const [stickerOpen, setStickerOpen] = useState(false);
  const patientId = patient.patientId;
  const patientName =
    patient.name ||
    [patient.firstName, patient.middleName, patient.lastName].filter(Boolean).join(' ') ||
    'patient';

  // No id → no actionable row (matches the list's `enableRowSelection`
  // guard). Render nothing rather than a dead menu.
  if (!patientId) return null;

  const run = (fn: () => void) => () => {
    setOpen(false);
    fn();
  };

  // Flutter `archivedOptions` (:742) — Edit only, always titled "Edit".
  // `!isMergedView` preserves Flutter's behavioral guard (no billing/appt
  // shortcuts on merged patients) — only the permission gate changed (B).
  const showBilling = !isArchivedView && canViewBilling && !isMergedView;
  const showArchive = !isArchivedView && canArchive;
  // Print sticker — Flutter `options:847` has NO permission/merged gate;
  // it's only absent from the archived menu (`archivedOptions`). So show it
  // in any active/merged view.
  const showSticker = !isArchivedView;
  // Create Appointment — Flutter `options:777-780`: `Manage_Patient_Appointments`
  // add AND not merged; absent from the archived menu.
  const showCreateAppointment =
    !isArchivedView && canCreateAppointment && !isMergedView;
  const editTitle =
    isArchivedView || canEditDemographics
      ? 'Edit Patient Details'
      : 'View Patient Details';

  return (
    <AnchoredPopover open={open} onOpenChange={setOpen}>
      <RowActionsTrigger ariaLabel="Patient actions" tooltip={`Actions for ${patientName}`}>
        {/* PROD-2743 §2.5: ON = the DS `more` glyph (one icon system); OFF = today's lucide. */}
        <MoreHorizontal className="size-4" aria-hidden />
      </RowActionsTrigger>
      <AnchoredPopoverContent collisionAvoidance={{ fallbackAxisSide: 'end' }} align="end" side="bottom" className="w-52 p-1 gap-3">
        {/* Order mirrors Flutter `options`: Create Appointment → Print
            sticker → View Billing → Edit/View → Archive. */}
        {showCreateAppointment ? (
          <MenuItem
            icon={Calendar}
            label="Create Appointment"
            onClick={run(() => onCreateAppointment(patient))}
          />
        ) : null}
        {showSticker ? (
          <MenuItem
            icon={Printer}
            label="Print Patient sticker"
            onClick={run(() => setStickerOpen(true))}
          />
        ) : null}
        {showBilling ? (
          <MenuItem
            icon={Receipt}
            label="View Billing Details"
            onClick={run(() => onViewBilling(patientId))}
          />
        ) : null}
        {onEnrolInCohort && !isArchivedView ? (
          <MenuItem
            icon={Plus}
            label="Enrol in cohort"
            onClick={run(() => onEnrolInCohort(patient))}
          />
        ) : null}
        <MenuItem
          icon={Pencil}
          label={editTitle}
          onClick={run(() => onEdit(patientId))}
        />
        {showArchive ? (
          <MenuItem
            icon={Archive}
            label="Archive Patient"
            destructive
            onClick={run(() => onArchive(patientId))}
          />
        ) : null}
      </AnchoredPopoverContent>

      <PatientStickerDialog
        open={stickerOpen}
        onOpenChange={setStickerOpen}
        patient={patient}
        brandingUrl={brandingUrl}
        enableDevTools={stickerDevTools}
      />
    </AnchoredPopover>
  );
}

export function MenuItem({
  icon: Glyph,
  label,
  onClick,
  destructive,
}: {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
  destructive?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-slot="row-menu-item"
      data-tone={destructive ? 'danger' : undefined}
      className="w-full flex items-center gap-3 px-3 py-1.5 text-left hover:bg-grey-100"
    >
      <Glyph className="size-4" aria-hidden />
      <span>{label}</span>
    </button>
  );
}
